import { MediaItem } from "shared/types";
import { PhotosForm } from "shared/app/PhotosForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { Unit } from "types/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { PhotoWithStatus } from "shared/app/PhotosForm/types";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

const getSmallMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "small");
};

const getFullMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "full");
};

const transformMedia = (media: Unit["media"]): PhotoWithStatus[] => {
  return (
    media?.map((media) => {
      const smallMedia = getSmallMedia(media);
      const fullMedia = getFullMedia(media);
      return {
        id: media.id.toString(),
        status: "uploaded",
        resolutions: {
          small: {
            file: new File([], ""),
            uploadedUrl: smallMedia?.url || ""
          },
          full: {
            file: new File([], ""),
            uploadedUrl: fullMedia?.url || ""
          }
        },
        previewUrl: smallMedia?.url || ""
      };
    }) || []
  );
};

interface PhotosSectionProps {
  propertyId: number;
  singleFamilyId?: number;
  propertyMedia: MediaItem[];
}

export const PhotosSection = ({
  propertyId,
  singleFamilyId,
  propertyMedia
}: PhotosSectionProps) => {
  const queryClient = useQueryClient();

  const { data: unitMedia } = useQuery({
    queryKey: ["unit", String(singleFamilyId)],
    queryFn: () => addressApi.getUnitById(String(singleFamilyId)),
    enabled: Boolean(singleFamilyId),
    select: (data) => data.media
  });

  const { mutate: uploadMedia, isPending } = useMutation({
    mutationFn: (media) => {
      if (singleFamilyId)
        return addressApi.editUnit(singleFamilyId, {
          media_session_key: sessionKey,
          media: media as any
        });

      return addressApi.editProperty({
        media_session_key: sessionKey,
        propertyId,
        media: media as any
      });
    },
    onError: (error) => {
      toast.error(`Failed to upload photos: ${getErrorMessage(error)}`);
    },
    onSuccess: () => {
      onSuccess();
    }
  });

  const onSuccess = singleFamilyId
    ? () =>
        queryClient.invalidateQueries({
          queryKey: ["unit", String(singleFamilyId)]
        })
    : () =>
        queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        });

  return (
    <PhotosForm
      onChange={(media) => uploadMedia(media as any)}
      isUploading={isPending}
      sessionKey={sessionKey}
      uploadedPhotos={transformMedia(
        singleFamilyId ? unitMedia || [] : propertyMedia
      )}
      onDeleteSuccess={onSuccess}
      onMakeDefaultSuccess={onSuccess}
    />
  );
};
